/**
 * Roles that see every lead and may assign anyone. Mirrors the backend's
 * `authorizeAdmin`: an account manager or super-admin working a school's CRM
 * acts as that school's admin.
 */
export const LEAD_ASSIGNMENT_ADMIN_ROLES = [
    "admin",
    "super_admin",
    "account_manager",
];
/**
 * Roles a lead can be assigned TO. Only the school's own people: account
 * managers and super-admins belong to no client (`clientId` null), so they
 * never appear in a school's team.
 */
export const LEAD_ASSIGNABLE_ROLES = ["admin", "member"];
export function isLeadAssignmentAdmin(role) {
    return LEAD_ASSIGNMENT_ADMIN_ROLES.includes(role);
}
export function resolveViewerScope(policy, viewer) {
    if (!policy.enabled)
        return null;
    if (viewer.role === "member") {
        return { profileId: viewer.id, includeUnassigned: !policy.autoAssign };
    }
    if (viewer.role === "admin" && policy.adminOwnLeadsOnly) {
        return { profileId: viewer.id, includeUnassigned: true };
    }
    return null;
}
/**
 * Whether a lead this user works from the CRM — answers or creates — becomes
 * theirs. Exactly the restricted viewers: an unowned lead someone in a scope
 * answers would otherwise stay in every scope, and a lead they create could be
 * dealt to someone else and vanish from their list the moment it is saved.
 * Under auto-assignment a member does not take on reply (the rotation deals),
 * which `claimOnReply` reads from `assignmentActionsFor`.
 */
export function isScopedViewer(policy, viewer) {
    return resolveViewerScope(policy, viewer) !== null;
}
/** Whether a lead with this assignee is inside the viewer's scope. */
export function canViewLead(scope, assignedProfileId) {
    if (scope === null)
        return true;
    if (assignedProfileId === scope.profileId)
        return true;
    return assignedProfileId === null && scope.includeUnassigned;
}
/**
 * May `actor` change this lead's assignee to `target` (`null` = unassign)?
 *
 * `target` must already be known to belong to the lead's client — the caller
 * resolves it from that client's team, so a foreign id arrives as "not found"
 * before this function runs.
 */
export function resolveAssignmentChange(input) {
    const { policy, actor, currentAssigneeId, target } = input;
    if (!policy.enabled)
        return { ok: false, code: "policy_disabled" };
    if (target !== null && (!target.active || !LEAD_ASSIGNABLE_ROLES.includes(target.role))) {
        return { ok: false, code: "invalid_target" };
    }
    const targetId = target?.id ?? null;
    if (targetId === currentAssigneeId)
        return { ok: false, code: "no_change" };
    // Admins always may — the transfer switch only governs members.
    if (isLeadAssignmentAdmin(actor.role)) {
        return { ok: true, reason: targetId === null ? "admin_unassign" : "admin_assign" };
    }
    if (currentAssigneeId === null) {
        // The only thing a member may do with an unowned lead is take it.
        if (targetId !== actor.id)
            return { ok: false, code: "not_owner" };
        if (policy.autoAssign)
            return { ok: false, code: "auto_mode" };
        return { ok: true, reason: "self_claim" };
    }
    if (currentAssigneeId !== actor.id)
        return { ok: false, code: "not_owner" };
    if (targetId === null) {
        if (policy.autoAssign)
            return { ok: false, code: "auto_mode" };
        return { ok: true, reason: "self_release" };
    }
    if (!policy.memberTransfer)
        return { ok: false, code: "transfer_disabled" };
    return { ok: true, reason: "transfer" };
}
export function assignmentActionsFor(policy, actor, currentAssigneeId) {
    const none = {
        canAssignAny: false,
        canClaim: false,
        canRelease: false,
        canTransfer: false,
    };
    if (!policy.enabled)
        return none;
    if (isLeadAssignmentAdmin(actor.role))
        return { ...none, canAssignAny: true };
    if (actor.role !== "member")
        return none;
    const owns = currentAssigneeId === actor.id;
    return {
        canAssignAny: false,
        canClaim: currentAssigneeId === null && !policy.autoAssign,
        canRelease: owns && !policy.autoAssign,
        canTransfer: owns && policy.memberTransfer,
    };
}
/** Spanish copy for each refusal — the endpoint's message and the CRM's toast. */
export const ASSIGNMENT_REFUSAL_MESSAGES = {
    policy_disabled: "La asignación de prospectos no está activada.",
    invalid_target: "Esa persona no puede recibir prospectos.",
    no_change: "El prospecto ya está asignado a esa persona.",
    auto_mode: "Con la asignación automática, solo un administrador puede cambiar al responsable.",
    not_owner: "Solo puedes cambiar al responsable de tus propios prospectos.",
    transfer_disabled: "Los miembros no pueden transferir prospectos. Pídeselo a un administrador.",
};
//# sourceMappingURL=lead-assignment-rules.js.map